/* "From the blog" panel. Moved verbatim from player/src/App.tsx; shared with
   the launcher, which passes the site origin and opens links externally. */
import { useEffect, useState } from 'react'
import { Blog } from './Icons'
import { SectionHead } from './section-head'
import type { BlogApi, FeaturedPost, RecentPost } from './types'

// Relative media paths stay same-origin on the site; the launcher prefixes them.
const abs = (origin: string, u: string) => (origin && u.startsWith('/') ? origin + u : u)

/* ------------------------------------------------------------------ Blog panel */
/* Full-width card between the live overview and Maps & mods, pointing at one
 * post. Which post is chosen in the admin; the endpoint falls back to the
 * newest published one, so this is never blank for want of a decision.
 * Renders nothing at all if the blog has no published posts or the request
 * fails, rather than leaving an empty card on the homepage. */
export function BlogPanel({ api, origin = '', external = false, logoSrc = '/logo.svg' }: {
  api: BlogApi; origin?: string; external?: boolean; logoSrc?: string
}) {
  const ext = external ? { target: '_blank', rel: 'noopener noreferrer' } : {}
  const [post, setPost] = useState<FeaturedPost | null>(null)
  // Newest posts besides the featured one, only where the client offers them.
  const [recent, setRecent] = useState<RecentPost[]>([])

  useEffect(() => {
    let live = true
    api.blogFeatured()
      .then((r) => { if (live) setPost(r.post) })
      .catch(() => {})
    api.blogRecent?.(4)
      .then((r) => { if (live) setRecent(Array.isArray(r) ? r : []) })
      .catch(() => {})
    return () => { live = false }
  }, [api])

  if (!post) return null
  const more = recent.filter((p) => p.slug !== post.slug).slice(0, 3)
  const dateOf = (p: RecentPost) => p.published_at
    ? new Date(p.published_at).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
    : ''
  const when = post.published_at
    ? new Date(post.published_at).toLocaleDateString(undefined,
        { day: 'numeric', month: 'long', year: 'numeric' })
    : ''

  return (
    <>
      <SectionHead icon={<Blog size={32} />} title="From the blog">
        <a href={`${origin}/blog`} {...ext} className="mono text-xs uppercase tracking-wider text-[var(--muted)]
                                   hover:text-[var(--foreground)] transition-colors">
          All posts
        </a>
      </SectionHead>
      <a href={`${origin}/blog/${post.slug}`} {...ext}
        className="grid sm:grid-cols-[minmax(0,1.05fr)_minmax(0,1fr)] rounded-2xl overflow-hidden
                   bg-[var(--background-secondary)] hover:bg-[var(--background-tertiary)]
                   transition-colors no-underline">
        {post.cover_url ? (
          <img src={abs(origin, post.cover_url)} alt={post.cover_alt || ''} loading="lazy" decoding="async"
            className="block w-full h-full object-cover aspect-[16/9] sm:aspect-auto sm:min-h-[240px]" />
        ) : (
          <div className="flex items-center justify-center bg-[var(--background-tertiary)]
                          aspect-[16/9] sm:aspect-auto sm:min-h-[240px]">
            <img src={logoSrc} alt="" className="w-24 opacity-20" />
          </div>
        )}
        <div className="p-6 sm:p-8 self-center">
          <div className="mono text-[10px] uppercase tracking-[0.14em] text-[var(--muted)]">
            {post.eyebrow || 'Field notes'}{when ? ` · ${when}` : ''}
          </div>
          {/* Anton uppercase, the same treatment the blog's own feature card and
              the site's h1 use. The font is already self-hosted and loaded, so
              this costs nothing extra. */}
          <h3 className="mt-3 text-[var(--foreground)]"
            style={{
              fontFamily: '"Anton", "Oswald", "Geist", -apple-system, system-ui, sans-serif',
              fontWeight: 400,
              fontSize: 'clamp(21px, 2.3vw, 30px)',
              lineHeight: 1.02,
              letterSpacing: '0.005em',
              textTransform: 'uppercase',
              margin: '12px 0 0',
            }}>
            {post.title}
          </h3>
          {post.excerpt && (
            <p className="mt-3 text-sm leading-relaxed text-[var(--muted)] max-w-[52ch]">
              {post.excerpt}
            </p>
          )}
          <span className="mono text-[11px] uppercase tracking-wider text-[var(--brand,#FF3C15)]
                           mt-5 inline-block">
            Read it
          </span>
        </div>
      </a>
      {more.length > 0 && (
        <div className="grid sm:grid-cols-3 gap-3 mt-3">
          {more.map((p) => (
            <a key={p.slug} href={`${origin}/blog/${p.slug}`} {...ext}
              className="rounded-2xl overflow-hidden bg-[var(--background-secondary)] hover:bg-[var(--background-tertiary)] transition-colors no-underline flex flex-col">
              {p.cover_url ? (
                <img src={abs(origin, p.cover_url)} alt={p.cover_alt || ''} loading="lazy" decoding="async"
                  className="block w-full object-cover aspect-[16/9]" />
              ) : (
                <div className="flex items-center justify-center bg-[var(--background-tertiary)] aspect-[16/9]">
                  <img src={logoSrc} alt="" className="w-16 opacity-20" />
                </div>
              )}
              <div className="p-4 flex flex-col flex-1">
                <div className="mono text-[10px] uppercase tracking-[0.14em] text-[var(--muted)]">
                  {p.eyebrow || 'Field notes'}{dateOf(p) ? ` · ${dateOf(p)}` : ''}{p.reading_minutes ? ` · ${p.reading_minutes} min` : ''}
                </div>
                <div className="mt-2 text-[15px] font-semibold leading-snug text-[var(--foreground)] line-clamp-2">{p.title}</div>
                {p.excerpt && <p className="mt-2 text-sm leading-relaxed text-[var(--muted)] line-clamp-2">{p.excerpt}</p>}
              </div>
            </a>
          ))}
        </div>
      )}
    </>
  )
}
